
import { Component, Vue, Watch } from 'vue-property-decorator'
import { User, Environment, ComponentType } from '@/models'
import * as ComponentTypes from '@/components/componentTypes'
import getEnv from '@/plugins/getEnv'
import LanguagePicker from './LanguagePicker.vue'

@Component({
  components: {
    LanguagePicker
  }
})
export default class AdminNavbar extends Vue {
  environment: Environment | null = null
  desktopApp = getEnv('VUE_APP_IS_DESKTOP')
  languagePickerOpen = false

  widthScreen: number = screen.width
  shouldShow = false
  collapsing = false
  settingActive = false
  collapse() {
    if (this.mini) return
    this.collapsing = true
    this.$nextTick(() => {
      this.collapsing = false
    })
    setTimeout(() => {
      this.mini = true
    }, 100)
  }

  mounted() {
    setTimeout(() => {
      this.shouldShow = true
    }, 200)
  }

  settings = [
    {
      title: 'AdminNavbar.script.settings.settings',
      icon: 'settings',
      href: { name: 'adminSettingsGeneral' }
    },
    {
      title: 'AdminNavbar.script.settings.Appearance',
      icon: 'palette',
      href: { name: 'adminSettingsAppearance' }
    },
    {
      title: 'AdminNavbar.script.settings.environmentUsers',
      icon: 'person',
      href: `/${this.$route.params.environmentId}/components/environmentUsers`
    },
    {
      title: 'AdminNavbar.script.settings.adminSettingsProfile',
      icon: 'contact_mail',
      href: { name: 'adminSettingsProfile' }
    },
    {
      title: 'AdminNavbar.script.settings.adminSettingsEnv',
      icon: 'api',
      href: { name: 'adminSettingsEnv' }
    },
    {
      title: 'AdminNavbar.script.settings.adminComponentBrowser',
      icon: 'search',
      href: { name: 'adminComponentBrowser' }
    },
    {
      title: 'AdminNavbar.script.settings.roles',
      icon: 'group',
      href: `/${this.$route.params.environmentId}/components/roles`
    },
    {
      title: 'AdminNavbar.script.settings.links',
      icon: 'attachment',
      href: `/${this.$route.params.environmentId}/components/links`
    }
  ]

  logic = [
    {
      title: 'AdminNavbar.script.logic.filters',
      icon: 'filter_list',
      href: `/${this.$route.params.environmentId}/components/filters`
    },
    {
      title: 'AdminNavbar.script.logic.validations',
      icon: 'spellcheck',
      href: `/${this.$route.params.environmentId}/components/validations`
    },
    {
      title: 'AdminNavbar.script.logic.hooks',
      icon: 'memory',
      href: `/${this.$route.params.environmentId}/components/hooks`
    },
    {
      title: 'AdminNavbar.script.logic.tasks',
      icon: 'event_available',
      href: `/${this.$route.params.environmentId}/components/tasks`
    }
  ]

  integrations = [
    {
      title: 'AdminNavbar.script.logic.endpoints',
      icon: 'language',
      href: `/${this.$route.params.environmentId}/components/endpoints`
    },
    {
      title: 'AdminNavbar.script.logic.api',
      icon: 'account_tree',
      disabled: getEnv('VUE_APP_IS_DESKTOP'),
      href: `/${this.$route.params.environmentId}/components/api`
    }
  ]

  miniChange() {
    this.mini = !this.mini
  }

  handleClose() {
    if (this.$vuetify.breakpoint.xs) {
      this.drawer = !this.drawer

      if (this.mini) this.miniChange()
      return
    }
    this.drawer = true
    this.miniChange()
  }

  @Watch('mini')
  overlayChange() {
    this.$store.commit('dashboard/setDesignOverlay', !this.mini)
  }

  get isDark(): boolean {
    return this.$store.state.app.darkMode
  }

  get mini(): boolean {
    if (this.$store.state.dashboard.miniDrawer) {
      this.collapsing = true
      this.$nextTick(() => {
        this.collapsing = false
      })
    }
    return this.$store.state.dashboard.miniDrawer
  }

  set mini(val: boolean) {
    this.$store.commit('dashboard/setMiniDrawer', val)
  }

  get drawer(): boolean {
    let result = this.$store.state.dashboard.drawer
    return result
  }

  set drawer(val: boolean) {
    this.$store.commit('dashboard/setDrawer', val)
  }

  get showButtonBack() {
    return this.widthScreen > 900
  }

  // -------------------------

  changeEnvironment(environmentId: any) {
    return this.$router.push({
      name:
        (this.$route.name!.indexOf('admin') === 0 && this.$route.name) ||
        'admin',
      params: {
        environmentId
      }
    })
  }

  async logout() {
    await this.$store.dispatch('auth/logout')
    await this.$router.push('/login')
    return
  }

  toggleDarkMode() {
    this.$store.commit('app/toggleDarkMode')
  }

  get currentView() {
    if (this.$route.name === 'home') {
      return {
        icon: 'home',
        label: this.$t('AdminNavbar.script.get_currentView_home.text')
      }
    }
    if (this.$route.name === 'adminUser') {
      return {
        icon: 'person',
        label: this.$t('AdminNavbar.script.get_currentView_adminUser.text')
      }
    }
    if (this.$route.name!.indexOf('admin') === 0) {
      return {
        icon: 'apps',
        label: this.currentEnvironment ? this.currentEnvironment.name : '',
        imageIcon:
          this.currentEnvironment?.loginLogo?.url ||
          this.currentEnvironment?.logo?.url
      }
    }
  }

  get currentEnvironment() {
    const firstEntry = this.$store.state.app.recents[0]
    if (firstEntry && firstEntry._id === this.envId) {
      return this.$store.state.app.recents[0]
    }
  }

  get envId() {
    return this.$route.params.environmentId
  }

  get loggedIn(): boolean {
    return this.$store.getters['auth/loggedIn']
  }

  get user(): User {
    return this.$store.state.auth.user
  }

  get userAvatar(): string {
    return this.$store.getters['auth/userAvatarURL']
  }

  get userRoleDisplay(): string {
    if (!this.user) return ''
    if (this.user.roles.includes('superAdmin')) {
      return this.$t(
        'AdminNavbar.script.get_userRoleDisplay.superAdmin'
      ) as string
    }
    if (this.user.roles.includes('admin')) {
      return this.$t('AdminNavbar.script.get_userRoleDisplay.admin') as string
    }
    return this.$t('AdminNavbar.script.get_userRoleDisplay.user') as string
  }

  /** Component types */
  componentTypes = ComponentTypes as Record<string, ComponentType<any>>

  get visibleComponentTypes() {
    return Object.keys(this.componentTypes)
      .map(
        (k) =>
          this.componentTypes[k] &&
          !this.componentTypes[k].hidden &&
          (!this.componentTypes[k].allowedEnvironments ||
            this.componentTypes[k].allowedEnvironments!.includes(this.envId)) &&
          this.componentTypes[k]
      )
      .filter((t) => t) as ComponentType<any>[]
  }

  get showPadding(): boolean {
    return this.$store.state.dashboard.miniDrawer
  }
}
